/** @type {Map<string, { path: string, expiresAt: number }>} */
const memoryCache = new Map();

const LS_PREFIX = "idx_ds_pair_v1:";
const DEFAULT_TTL_MS = 30 * 60 * 1000;

function storageKey(mint) {
  return `${LS_PREFIX}${mint}`;
}

/**
 * @param {string} mint
 * @returns {string | null} Cached embed path segment (pair address or mint), or null if miss/expired.
 */
export function getCachedDexScreenerEmbedPath(mint) {
  const key = String(mint || "").trim();
  if (!key) return null;

  const mem = memoryCache.get(key);
  if (mem && mem.expiresAt > Date.now()) {
    return mem.path;
  }
  if (mem) memoryCache.delete(key);

  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(storageKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const path = typeof parsed?.path === "string" ? parsed.path.trim() : "";
    const expiresAt = Number(parsed?.expiresAt);
    if (!path || !Number.isFinite(expiresAt)) {
      window.localStorage.removeItem(storageKey(key));
      return null;
    }
    if (expiresAt <= Date.now()) {
      window.localStorage.removeItem(storageKey(key));
      return null;
    }
    memoryCache.set(key, { path, expiresAt });
    return path;
  } catch {
    try {
      window.localStorage.removeItem(storageKey(key));
    } catch {
      /* noop */
    }
    return null;
  }
}

/**
 * @param {string} mint
 * @param {string} path — Final URL segment for DexScreener embed (pair address or mint).
 * @param {number} [ttlMs=DEFAULT_TTL_MS]
 */
export function setCachedDexScreenerEmbedPath(mint, path, ttlMs = DEFAULT_TTL_MS) {
  const key = String(mint || "").trim();
  const p = String(path || "").trim();
  if (!key || !p) return;

  const expiresAt = Date.now() + Math.max(60_000, ttlMs);
  memoryCache.set(key, { path: p, expiresAt });

  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      storageKey(key),
      JSON.stringify({ path: p, expiresAt }),
    );
  } catch {
    /* quota or private mode */
  }
}
